import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match } from 'react-router-dom';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas } from '../../../local-types';
// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { LightStructure, TParams } from '../../../redux/reducers/api-types';
import {
    actions as partnerActions,
    makeMemoizedGetModelsName,
    selectors as partnerSelectors,
    types as partnerTypes,
} from '../../../redux/reducers/partners';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';

interface PartnerAddVehicleProps extends WithTranslation {
    match: match<TParams>;
    vehicleModels: LightStructure[];
    vehicleColors: LightStructure[];
    vehicleMotorizations: LightStructure[];
    savePartnerVehicleError: ErrorPayload;
    getVehicleModelsMotorizationAndColorsError: ErrorPayload;
    saveVehicle: (id: string, payload: any) => void;
    isSavingVehicle: boolean;
    getPartnerModels: () => void;
    getPartnerColors: () => void;
    getPartnerMotorizations: () => void;
}

interface PartnerAddVehicleState {
    formData: any;
}

class PartnerAddVehiclePage extends React.Component<PartnerAddVehicleProps, PartnerAddVehicleState> {
    constructor(props: PartnerAddVehicleProps) {
        super(props);
        const { match } = props;
        const id = match.params.id;
        this.state = {
            formData: {
                partner_id: id,
                registration_number: '',
                vehicle_model_id: '',
                vehicle_color_id: '',
                vehicle_motorization_id: '',
                insurance_validity_date: '',
                immatriculation_date: '',
                dispatch_groups: ['normal'],
                the_dispatch_groups: { normal: true },
                insurance_attachment_write: '',
                green_card_attachment_write: '',
                grey_card_attachment_write: '',
                technical_control_attachment_write: '',
            },
        };
    }

    componentDidMount() {
        const {
            vehicleModels,
            vehicleColors,
            vehicleMotorizations,
            getPartnerModels,
            getPartnerColors,
            getPartnerMotorizations,
        } = this.props;

        if (Object.entries(vehicleModels).length === 0) {
            getPartnerModels();
        }
        if (Object.entries(vehicleColors).length === 0) {
            getPartnerColors();
        }
        if (Object.entries(vehicleMotorizations).length === 0) {
            getPartnerMotorizations();
        }
    }

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateInputs = (): FormDatas[] => {
        const { t, vehicleModels, vehicleColors, vehicleMotorizations } = this.props;
        if (vehicleModels?.length > 0 && vehicleColors?.length > 0) {
            const inputs: FormDatas[] = [
                {
                    title: t('add_vehicle.title'),
                    datas: [
                        {
                            id: 'vehicle_model_id',
                            label: t('add_vehicle.model'),
                            options: vehicleModels,
                            required: true,
                        },
                        {
                            id: 'vehicle_color_id',
                            label: t('add_vehicle.color'),
                            options: vehicleColors,
                            required: true,
                        },
                        {
                            id: 'vehicle_motorization_id',
                            label: t('add_vehicle.motorization'),
                            options: vehicleMotorizations,
                            required: true,
                        },
                        {
                            id: 'registration_number',
                            label: t('add_vehicle.imma'),
                            required: true,
                            placeholder: 'XXX-555-XXX',
                        },
                        {
                            id: 'insurance_validity_date',
                            label: t('add_vehicle.valid_insurance'),
                            type: 'date',
                            required: true,
                        },
                        {
                            id: 'immatriculation_date',
                            label: t('add_vehicle.date_imma'),
                            type: 'date',
                            required: true,
                        },
                    ],
                },
                {
                    title: t('common:form.documents'),
                    datas: [
                        {
                            id: 'insurance_attachment_write',
                            label: t('add_vehicle.att_insurance'),
                            type: 'file',
                            required: true,
                        },
                        {
                            id: 'green_card_attachment_write',
                            label: t('add_vehicle.green_card'),
                            type: 'file',
                            required: true,
                        },
                        {
                            id: 'grey_card_attachment_write',
                            label: t('add_vehicle.grey_card'),
                            type: 'file',
                            required: true,
                        },
                        {
                            id: 'technical_control_attachment_write',
                            label: t('add_vehicle.tech_control'),
                            type: 'file',
                        },
                    ],
                },
            ];
            return inputs;
        }
        return [];
    };

    onSubmitVehicle = (formData: any, event, stringify: any, keepFormData: any) => {
        const { saveVehicle, match } = this.props;
        const id = match.params.id;
        const data = stringify;
        data.append('partner_id', id);

        this.setState({
            formData: keepFormData,
        });
        saveVehicle(id, data);
    };

    render() {
        const { formData } = this.state;
        const { match, savePartnerVehicleError, getVehicleModelsMotorizationAndColorsError, isSavingVehicle } =
            this.props;
        const id = match.params.id;

        return (
            <App needRights={{ name: AccountRight.PARTNER_SETTINGS, mode: RightRank.W }}>
                <ErrorMessage payload={savePartnerVehicleError} />
                <ErrorMessage payload={getVehicleModelsMotorizationAndColorsError} />
                <EnhancedForm
                    payload={formData}
                    inputs={this.generateInputs()}
                    backRoute={`${paths.partners}/${id}`}
                    onSubmit={this.onSubmitVehicle}
                    loader={isSavingVehicle}
                />
            </App>
        );
    }
}

const PartnerAddVehiclePageWithTranslation = withTranslation(['partner', 'common'])(PartnerAddVehiclePage);

const makemapStateToProps = () => {
    const getPartnerVehicleModels = makeMemoizedGetModelsName();

    const mapStateToProps = (state, ownProps) => {
        return {
            vehicleModels: getPartnerVehicleModels(state, ownProps),
            vehicleColors: partnerSelectors.vehicleColors(state),
            vehicleMotorizations: partnerSelectors.vehicleMotorizations(state),
            savePartnerVehicleError: errorSelectors.savePartnerVehicleError(state),
            isSavingVehicle: pendingSelectors.isPending(state, partnerTypes.SAVE_PARTNER_VEHICLE),
            getVehicleModelsMotorizationAndColorsError:
                errorSelectors.getVehicleModelsMotorizationAndColorsError(state),
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = {
    saveVehicle: partnerActions.saveVehicle,
    getPartnerModels: partnerActions.getPartnerModels,
    getPartnerColors: partnerActions.getPartnerColors,
    getPartnerMotorizations: partnerActions.getPartnerMotorizations,
};

export default connect(makemapStateToProps, mapDispatchToProps)(PartnerAddVehiclePageWithTranslation);
